import {Link} from "react-router-dom";
import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { ArrowRight, CalendarCheck, Check, Clock3, MapPin, MessageCircle, ShieldCheck, Trophy, Volleyball } from "lucide-react";
import type { Court } from "@arena/shared";
import { formatMoney } from "@arena/shared";
import { api } from "../api";
import { Logo } from "../components/Logo";

type Slot = { startsAt: string; endsAt: string; label: string; priceCents: number };

export function LandingPage() {
  const [courts, setCourts] = useState<Court[]>([]);
  const [courtId, setCourtId] = useState("");
  const [date, setDate] = useState(format(addDays(new Date(), 1), "yyyy-MM-dd"));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [durations, setDurations] = useState<Slot[]>([]);
  const [loadingDurations, setLoadingDurations] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [whatsappUrl, setWhatsappUrl] = useState<string | null>(null);

  useEffect(() => {
    api<{ whatsappUrl: string | null }>("/api/public/config").then((response) => setWhatsappUrl(response.whatsappUrl)).catch(() => undefined);
    api<{ courts: Court[] }>("/api/public/courts").then(({ courts: found }) => {
      setCourts(found); setCourtId(found[0]?.id ?? "");
    }).catch(() => setError("A agenda esta temporariamente indisponivel."));
  }, []);

  useEffect(() => {
    setSlot(null); setSlots([]); setDurations([]); setError("");
    if (!courtId || !date) { setLoadingSlots(false); return; }
    let active = true;
    setLoadingSlots(true);
    api<{ slots: Slot[] }>(`/api/public/availability?courtId=${courtId}&date=${date}`)
      .then((response) => { if (active) setSlots(response.slots); })
      .catch((requestError: Error) => { if (active) setError(requestError.message); })
      .finally(() => { if (active) setLoadingSlots(false); });
    return () => { active = false; };
  }, [courtId, date]);

  const startsAt = slot?.startsAt;
  useEffect(() => {
    if (!startsAt) { setLoadingDurations(false); setDurations([]); return; }
    let active = true;
    setDurations([]); setLoadingDurations(true); setError("");
    api<{ slots: Slot[] }>(`/api/public/booking-durations?courtId=${courtId}&date=${date}&startsAt=${encodeURIComponent(startsAt)}`)
      .then(response => { if (active) { setDurations(response.slots); setSlot(response.slots[0] ?? null); } })
      .catch((err: Error) => { if (active) { setSlot(null); setError(err.message); } })
      .finally(() => { if (active) setLoadingDurations(false); });
    return () => { active = false; };
  }, [courtId, date, startsAt]);

  async function book(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    if (!slot || loadingDurations || !durations.length) { setError("Escolha um horario disponivel."); return; }
    const data = new FormData(event.currentTarget);
    try {
      await api("/api/public/bookings", { method: "POST", body: JSON.stringify({
        courtId, startsAt: slot.startsAt, endsAt: slot.endsAt, priceCents: slot.priceCents,
        customer: { name: data.get("name"), phone: data.get("phone"), email: data.get("email") || null },
      }) });
      setSubmitted(true);
    } catch (requestError) { setError((requestError as Error).message); }
  }

  return (
    <div className="landing">
      <header className="landing-header"><Logo /><nav><Link to="/campeonatos">Campeonatos</Link><a href="#arena">A arena</a><a href="#agendar">Agendar</a><a className="button button-ghost button-small" href="/entrar">Área da equipe</a></nav></header>
      <main>
        <section className="hero">
          <div className="hero-glow hero-glow-one" /><div className="hero-glow hero-glow-two" />
          <div className="hero-copy">
            <span className="eyebrow"><span className="live-dot" /> Varginha ganhou uma nova arena</span>
            <h1>ONDE O JOGO<br /><em>ACONTECE.</em></h1>
            <p>Esporte, energia e resenha em um só lugar. Reserve sua quadra de beach tennis, vôlei de areia ou futevôlei.</p>
            <div className="hero-actions"><a className="button button-primary" href="#agendar">Agendar agora <ArrowRight size={19} /></a><a className="button button-ghost" href={whatsappUrl ?? "#agendar"} target={whatsappUrl ? "_blank" : undefined} rel="noreferrer"><MessageCircle size={19} /> Agendar pelo WhatsApp</a></div>
            <div className="hero-trust"><span><ShieldCheck /> Reserva segura</span><span><CalendarCheck /> Agenda em tempo real</span></div>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="arena-sun" /><div className="arena-net"><span /><span /><span /><span /></div>
            <div className="sport-card sport-card-one"><Volleyball /><span>VÔLEI<br />DE AREIA</span></div>
            <div className="sport-card sport-card-two"><Trophy /><span>BEACH<br />TENNIS</span></div>
            <div className="hero-ball"><span /></div><div className="sand-line" />
          </div>
        </section>

        <section className="sports-strip" id="arena">
          <div><Volleyball /><span><strong>3 modalidades</strong><small>Uma só arena</small></span></div>
          <div><Clock3 /><span><strong>Agenda online</strong><small>Reserve em segundos</small></span></div>
          <div><MapPin /><span><strong>Varginha, MG</strong><small>Esporte e conexão</small></span></div>
        </section>

        <section className="booking-section" id="agendar">
          <div className="booking-intro"><span className="eyebrow">SEU PRÓXIMO JOGO</span><h2>Escolha a quadra.<br /><em>A gente prepara a areia.</em></h2><p>Consulte os horários livres e solicite sua reserva. Nossa equipe confirma tudo com você.</p><ul><li><Check /> Sem ligações</li><li><Check /> Preços transparentes</li><li><Check /> Confirmação pelo WhatsApp</li></ul></div>
          <div className="booking-card">
            {submitted ? <div className="success-state"><span><Check /></span><h3>Solicitação recebida!</h3><p>Registramos sua solicitação e nossa equipe vai confirmar os detalhes com você.</p><button className="button button-primary" onClick={() => setSubmitted(false)}>Fazer outra reserva</button></div> :
              <form onSubmit={book}>
                <div className="form-heading"><span>01</span><div><h3>Monte sua reserva</h3><p>Escolha a quadra, a data e o horário.</p></div></div>
                <label>Quadra<select value={courtId} onChange={(event) => setCourtId(event.target.value)} required>{courts.map((court) => <option key={court.id} value={court.id}>{court.name}</option>)}</select></label>
                <label>Data<input type="date" value={date} min={format(new Date(), "yyyy-MM-dd")} onChange={(event) => setDate(event.target.value)} required /></label>
                <div className="field-label">Horários disponíveis</div>
                <div className="slots-grid">{loadingSlots ? <span className="slots-loading"><span className="spinner" /> Consultando agenda...</span> : slots.length ? slots.map((candidate) => <button type="button" key={candidate.startsAt} className={slot?.startsAt === candidate.startsAt ? "selected" : ""} onClick={() => setSlot(candidate)}><strong>{candidate.label}</strong><small>{formatMoney(candidate.priceCents)}</small></button>) : <p className="no-slots">Nenhum horário livre nesse dia.</p>}</div>
                {loadingDurations ? <p className="slots-loading">Calculando duração e valores…</p> : !!durations.length && <label>Duração<select value={slot?.endsAt ?? ""} onChange={event => setSlot(durations.find(candidate => candidate.endsAt === event.target.value) ?? null)}>{durations.map(candidate => <option key={candidate.endsAt} value={candidate.endsAt}>{candidate.label} · {formatMoney(candidate.priceCents)}</option>)}</select></label>}
                {slot && !loadingDurations && !!durations.length && <p className="form-notice">Valor total: {formatMoney(slot.priceCents)}</p>}
                <div className="form-divider" />
                <div className="two-columns"><label>Seu nome<input name="name" placeholder="Nome completo" required minLength={2} /></label><label>WhatsApp<input name="phone" placeholder="(35) 99999-9999" required minLength={10} /></label></div>
                <label>E-mail <small>(opcional)</small><input name="email" type="email" placeholder="voce@email.com" /></label>
                {error && <div className="form-error">{error}</div>}
                <button className="button button-primary button-full" type="submit" disabled={!slot || loadingSlots || loadingDurations || !durations.length}>Solicitar reserva <ArrowRight size={19} /></button>
              </form>}
          </div>
        </section>
      </main>
      <footer><Logo /><p>Esporte · Conexão · Superação</p><a href="/politica-de-privacidade">Política de Privacidade</a><span>© {new Date().getFullYear()} Arena Sul Minas</span></footer>
    </div>
  );
}
